import React from 'react';

// Factor display configurations
const FACTOR_CONFIG = {
  anomaly_frequency: {
    label: 'Anomaly Frequency',
    icon: '\u26A0\uFE0F',
    description: 'Frequency of detected anomalies',
    color: '#EF4444',
  },
  high_risk_zone_visits: {
    label: 'High-Risk Zones',
    icon: '\uD83D\uDCCD',
    description: 'Visits to high-risk areas',
    color: '#F59E0B',
  },
  association_risk: {
    label: 'Association Risk',
    icon: '\uD83D\uDC65',
    description: 'Network and association analysis',
    color: '#8B5CF6',
  },
  compliance_violations: {
    label: 'Compliance',
    icon: '\uD83D\uDCCB',
    description: 'Rule violations and compliance issues',
    color: '#F97316',
  },
  pattern_stability: {
    label: 'Pattern Stability',
    icon: '\uD83D\uDCC8',
    description: 'Deviation from established routines',
    color: '#3B82F6',
  },
  recency_factor: {
    label: 'Recent Events',
    icon: '\u23F0',
    description: 'Time-weighted recent activity',
    color: '#6366F1',
  },
};

// Impact level configurations
const IMPACT_CONFIG = {
  high: {
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    label: 'High Impact',
  },
  medium: {
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    label: 'Medium Impact',
  },
  low: {
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    label: 'Low Impact',
  },
  minimal: {
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-200',
    label: 'Minimal Impact',
  },
};

/**
 * Individual factor bar for waterfall visualization
 */
const FactorBar = ({ factorKey, data, maxContribution }) => {
  const config = FACTOR_CONFIG[factorKey] || {
    label: factorKey,
    icon: '\u2022',
    description: '',
    color: '#6B7280',
  };

  const contribution = data?.contribution || 0;
  const value = data?.value || 0;
  const barWidth = (contribution / maxContribution) * 100;

  // Determine impact level based on contribution (1-5 scale: max contribution per factor ~0.88)
  let impact = 'minimal';
  if (contribution >= 0.8) impact = 'high';
  else if (contribution >= 0.4) impact = 'medium';
  else if (contribution >= 0.2) impact = 'low';

  const impactConfig = IMPACT_CONFIG[impact];

  return (
    <div className="mb-3">
      {/* Factor header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-base">{config.icon}</span>
          <span className="text-sm font-medium text-gray-700">{config.label}</span>
          {contribution >= 0.4 && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${impactConfig.bgColor} ${impactConfig.textColor}`}
            >
              {impactConfig.label}
            </span>
          )}
        </div>
        <span className="text-sm font-semibold" style={{ color: config.color }}>
          +{contribution.toFixed(2)}
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-4 bg-gray-100 rounded overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full rounded transition-all duration-500"
          style={{
            width: `${Math.max(barWidth, 2)}%`,
            backgroundColor: config.color,
            opacity: 0.8,
          }}
        />
        <div
          className="absolute left-0 top-0 h-full flex items-center justify-end pr-2 text-xs font-medium"
          style={{
            width: `${Math.max(barWidth, 10)}%`,
            color: barWidth > 30 ? 'white' : 'transparent',
          }}
        >
          {(value * 100).toFixed(0)}%
        </div>
      </div>

      {/* Description */}
      {config.description && (
        <p className="text-xs text-gray-500 mt-1">{config.description}</p>
      )}
    </div>
  );
};

/**
 * Recommendations section
 */
const RecommendationsSection = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
        Recommendations
      </h4>
      <ul className="space-y-2">
        {recommendations.map((rec, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="text-blue-500 mt-0.5">{'\u2022'}</span>
            <span>{rec}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * RiskFactorBreakdown - SHAP-like waterfall chart for factor contributions
 */
const RiskFactorBreakdown = ({ factorExplanation, showRecommendations = true }) => {
  if (!factorExplanation) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Factor Breakdown</h3>
        <div className="text-center py-6 text-gray-500 text-sm">
          <p>No factor breakdown available.</p>
        </div>
      </div>
    );
  }

  const {
    factorExplanations,
    topFactors,
    recommendations,
    score,
    category,
  } = factorExplanation;

  // Sort factors by contribution (descending)
  const sortedFactors = Object.entries(factorExplanations || {})
    .sort((a, b) => (b[1]?.contribution || 0) - (a[1]?.contribution || 0));

  // Find max contribution for scaling bars (max possible single-factor contribution is ~0.88)
  const maxContribution = Math.max(
    ...sortedFactors.map(([_, data]) => data?.contribution || 0),
    0.8 // Minimum scale
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Factor Breakdown</h3>
        {topFactors && topFactors.length > 0 && (
          <div className="text-xs text-gray-500">
            Top factors: {topFactors.join(', ')}
          </div>
        )}
      </div>

      {/* Recommendations */}
      {showRecommendations && (
        <RecommendationsSection recommendations={recommendations} />
      )}

      {/* Summary */}
      {score !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-base">{'\uD83D\uDEE1\uFE0F'}</span>
              <span className="text-sm font-medium text-gray-700">Total Risk Score</span>
            </div>
            <span className="text-sm font-semibold text-gray-800">
              {score.toFixed(2)} / 5.00
            </span>
          </div>
          <div className="relative h-4 bg-gray-100 rounded overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full rounded transition-all duration-500"
              style={{
                width: `${Math.max(((score - 1) / 4) * 100, 2)}%`,
                background: `linear-gradient(to right, #22C55E, #F59E0B, #EF4444)`,
                opacity: 0.8,
              }}
            />
          </div>
        </div>
      )}

      {/* Factor bars */}
      <div className="space-y-1">
        {sortedFactors.map(([factorKey, data]) => (
          <FactorBar
            key={factorKey}
            factorKey={factorKey}
            data={data}
            maxContribution={maxContribution}
          />
        ))}
      </div>
    </div>
  );
};

export default RiskFactorBreakdown;
export { FactorBar, RecommendationsSection, FACTOR_CONFIG, IMPACT_CONFIG };
